import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Rating,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { IconHandGrab, IconLock, IconPackage, IconTag } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "../../../gql/__generated__/graphql";
import { isLoggedIn } from "../../utils/jwtUtils";
import CollabModal, {
  BrandCollabFormValues,
  CreatorCollabFormValues,
} from "../../pages/DiscoverPage/components/CollabModal";

interface BrandCardProps {
  brand: Brand;
  setupComplete?: boolean; // Whether the user's profile setup is complete
}

export function BrandCard({ brand, setupComplete = true }: BrandCardProps) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userIsLoggedIn = isLoggedIn();

  // Format numbers (e.g., 1500 -> 1.5K)
  const formatNumber = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    }
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  // Format currency (e.g., 15000 -> $15,000)
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleCardClick = () => {
    navigate(`/profile/${brand.id}`);
  };

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      onClick={isModalOpen ? undefined : handleCardClick}
      style={{ cursor: isModalOpen ? "default" : "pointer", transition: "transform 0.2s, box-shadow 0.2s" }}
      onMouseEnter={(e) => {
        if (!isModalOpen) {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "";
      }}
    >
      <Flex
        direction={{ base: "column", sm: "row" }}
        gap="md"
        align={{ base: "center", sm: "flex-start" }}
      >
        {/* Logo */}
        <Avatar
          src={brand.logo}
          size={80}
          radius={80}
          alt={`${brand.name}'s logo`}
        />

        {/* Brand info */}
        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between" wrap="nowrap">
            <Box>
              <Text fw={700} size="xl">
                {brand.name}
              </Text>
              <Group gap="xs">
                <Rating value={brand.rating ?? 0} readOnly fractions={2} />
                <Text size="sm" c="dimmed">
                  ({brand.rating?.toFixed(1)})
                </Text>
              </Group>
            </Box>

            {/* Products count badge - visible on mobile and desktop */}
            <Badge
              size="lg"
              variant="light"
              leftSection={<IconPackage size={14} />}
              styles={{
                root: {
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                  overflow: "visible",
                },
                label: {
                  overflow: "visible",
                  textOverflow: "clip",
                },
              }}
            >
              {formatNumber(brand.productsCount)} Products
            </Badge>
          </Group>

          <Text size="sm" lineClamp={3}>
            {brand.description}
          </Text>
        </Stack>
      </Flex>

      {/* Categories and Monthly GMV */}
      <SimpleGrid cols={2} mt="md">
        <Group gap="xs">
          {brand.categories.map((category) => (
            <Badge
              key={category}
              variant="outline"
              size="sm"
              leftSection={<IconTag size={12} />}
            >
              {category}
            </Badge>
          ))}
        </Group>
      </SimpleGrid>

      <Divider my="md" />

      {/* Completed Collaborations */}
      <Flex gap="md" wrap="wrap" justify="space-between" align="center">
        <Group gap="xs">
          <ActionIcon
            variant="light"
            color={theme.colors.blue[6]}
            size="lg"
            radius="xl"
          >
            <IconHandGrab size={18} />
          </ActionIcon>
          <Stack gap={0}>
            <Text size="xs" tt="uppercase" fw={500}>
              Collabs
            </Text>
            <Text size="sm" fw={700}>
              {formatNumber(brand.completedCollabs)}
            </Text>
          </Stack>
        </Group>
        <Tooltip
          label={
            !userIsLoggedIn
              ? "Please log in or sign up to collaborate with brands"
              : !setupComplete
                ? "Complete your profile setup to start collaborating"
                : ""
          }
          disabled={userIsLoggedIn && setupComplete}
          position="top"
          withArrow
        >
          <Button
            onClick={(e) => {
              e.stopPropagation();
              if (userIsLoggedIn && setupComplete) {
                setIsModalOpen(true);
              } else if (!userIsLoggedIn) {
                navigate("/login");
              }
            }}
            disabled={!userIsLoggedIn || !setupComplete}
            leftSection={!userIsLoggedIn || !setupComplete ? <IconLock size={16} /> : undefined}
          >
            {!userIsLoggedIn ? "Log In to Collab" : !setupComplete ? "Complete Profile" : "Collab"}
          </Button>
        </Tooltip>
      </Flex>

      <CollabModal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isBrand={false}
        brand={brand}
        onSubmit={function (
          values: BrandCollabFormValues | CreatorCollabFormValues
        ) {
          console.log("successful collab sent", values);
        }}
      />
    </Card>
  );
}
